import dayjs from 'dayjs';

import { getCalendarEvents, fetchRemainingCalendarEvents, getExternalCalendarEvents } from 'api/events';
import {
  parseEvent,
  parseChecklistEvent,
  parsePaymentEvent,
  parseWeddingDateEvent,
  parseBookingEvent,
  parseBookedLeadEvent,
} from 'components/Calendar/utils/parseEvents';
import { SET_WEDDINGS_PERMISSIONS } from 'containers/Projects/types';
import { UPDATE_TASK_STORE_BY_ID } from 'containers/App/constants';
import { objToCamelCase } from 'utils/general';
import * as types from './types';

const fetchRemainingCalEvents = async (eventsMeta, handleEvents) => {
  const loadNextEvents = async ({ calendarId, nextCursor, dateStart, dateEnd }) => {
    const response = await fetchRemainingCalendarEvents({ calendarId, nextCursor, dateStart, dateEnd });

    /*
    const { error } = response;
    if (error) {
      setErrorModal(error);

      return false;
    }
    */

    const newEvents = response.events?.map(parseEvent) || [];
    if (newEvents.length && typeof handleEvents === 'function') {
      handleEvents(newEvents);
    }

    return response?.next_cursor;
  };

  const {
    next_cursors: initialCursors = [],
    date_start: dateStart,
    date_end: dateEnd,
  } = eventsMeta || {};

  /* eslint-disable no-restricted-syntax, no-await-in-loop */
  for (const { calendar_id: calendarId, next_cursor: initialCursor } of initialCursors) {
    let currentCursor = await loadNextEvents({ calendarId, nextCursor: initialCursor, dateStart, dateEnd });

    while (currentCursor) {
      currentCursor = await loadNextEvents({ calendarId, nextCursor: currentCursor, dateStart, dateEnd });
    }
  }
  /* eslint-enable no-restricted-syntax, no-await-in-loop */
};

export const fetchEvents = (start, end, includePermissions = false) => {
  return async (dispatch) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const response = await getCalendarEvents({
      start: dayjs(startDate).format('YYYYMMDD'),
      end: dayjs(endDate).format('YYYYMMDD'),
      permissions: includePermissions,
    });

    if (response.error) {
      // #TODO: Might want to create a failure type to spawn an error modal.
      dispatch({
        type: types.ADD_EVENTS,
        events: {},
      });
    }

    // Normalize each event for use in the calendar. Each event
    // type can have their own unique shape.
    const eventPromises = [
      ...(response?.events || []).map(parseEvent),
      ...(response?.checklist_items || []).map((event) =>
        parseChecklistEvent(event, objToCamelCase(response?.checklist_assignments))),
      ...(response?.payments || []).map(parsePaymentEvent),
      ...(response?.project_dates || []).map(parseWeddingDateEvent),
      ...(response?.bookings || []).map(parseBookingEvent),
      ...(response?.booked_leads || []).map(parseBookedLeadEvent),
    ];

    const events = await Promise.all(eventPromises.flat());

    dispatch({
      type: types.ADD_EVENTS,
      events,
    });

    if (Object.keys(response?.permissions || {}).length) {
      dispatch({
        type: SET_WEDDINGS_PERMISSIONS,
        permissions: response.permissions,
      });
    }

    // Fetch additional events via cursor data. The callback in the
    // second arg dispatches the results of each recursive call.
    if (response.events_meta) {
      fetchRemainingCalEvents(response.events_meta, (_events) =>
        dispatch({
          type: types.ADD_EVENTS,
          events: _events.flat(),
        }),
      ).then(() => {
        dispatch({
          type: types.FINISH_LOADING_EVENTS,
        });
      });
    } else {
      dispatch({
        type: types.FINISH_LOADING_EVENTS,
      });
    }
  };
};

export const fetchExternalEvents = (start, end) => {
  return async (dispatch) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const response = await getExternalCalendarEvents({
      start: dayjs(startDate).format('YYYYMMDD'),
      end: dayjs(endDate).format('YYYYMMDD'),
    });

    if (response.error) {
      // #TODO: Might want to create a failure type to spawn an error modal.
      dispatch({
        type: types.ADD_EVENTS,
        events: {},
      });
    }

    const events = response?.events.map(parseEvent);

    dispatch({
      type: types.ADD_EVENTS,
      events,
    });

    // Fetch additional events via cursor data. The callback in the
    // second arg dispatches the results of each recursive call.
    if (response.events_meta) {
      fetchRemainingCalEvents(response.events_meta, (_events) =>
        dispatch({
          type: types.ADD_EVENTS,
          events: _events.flat(),
        }),
      ).then(() => {
        dispatch({
          type: types.FINISH_LOADING_EVENTS,
        });
      });
    } else {
      dispatch({
        type: types.FINISH_LOADING_EVENTS,
      });
    }
  };
};

// #TODO: This might make more sense being in the tasks action file.
export const updateTaskStore = ({ id, taskType, data }) => {
  return {
    type: UPDATE_TASK_STORE_BY_ID,
    module: taskType,
    payload: {
      id,
      data,
    },
  };
};

export const updateEventStore = ({ id, data, deleting = false }) => {
  if (deleting) {
    return {
      type: types.REMOVE_EVENT,
      payload: { id },
    };
  }
  return {
    type: types.UPDATE_EVENT,
    payload: { id, data },
  };
};
